/**
 * „Wo die Angebote/Aufträge stehen" als Bahnen (STATIONEN-BAHNEN.md).
 *
 * Die Übersicht zeigt nicht mehr Prozessfarben, sondern **wer dran ist**:
 * eine Bahn je Verantwortlichem, und die Kachel eines Schritts steht in der
 * Bahn dessen, bei dem der Vorgang gerade liegt. Damit beantwortet ein Blick
 * die Frage, die man wirklich hat – was liegt bei uns, was beim Kunden.
 *
 * Ein Bauteil für beide Masken: Angebote haben zwei Bahnen und rechts die
 * Wiedervorlage, Aufträge drei und keine. Zwei Fassungen wären zwei Stellen,
 * an denen dieselbe Regel steht.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { useKora } from '../zustand';
import {
  BAHNEN_VORGABE,
  BAHNFARBEN,
  type BahnId,
  bahnenfarbenLesen,
  farbeVon,
  tonleiter,
} from '../hilfen/bahnen';
import type { Nutzer } from '../typen';
import './stationenbahnen.css';

export interface BahnSchritt {
  key: string;
  /** Die Bezeichnung in der Kachel – der volle Name, er darf umbrechen. */
  label: string;
  anzahl: number;
  /** Ungeöffnete Vorgänge – der rote Kreis rechts oben (2.5). */
  neu: number;
  /**
   * Gehört dieser Schritt in diese Bahn?
   *
   * Jede Spalte ist ein Schritt und steht in jeder Bahn – die Kachel aber
   * nur bei der Partei, die dran ist. Die anderen Bahnen zeigen an dieser
   * Stelle nichts als ihre Fläche; so liest man die Spalte senkrecht.
   */
  eigen: boolean;
}

export interface Bahn {
  id: BahnId;
  name: string;
  /** Fertig formatiert, etwa „128 400 €" oder „ohne Summe". */
  summe: string;
  /**
   * Keine Geldsumme, sondern ein Ersatz – dann tritt die Zeile zurück.
   *
   * „ohne Summe" in 20 px/700 schriee lauter als jede echte Zahl daneben;
   * der Kopf soll aber die Summe betonen, nicht ihr Fehlen.
   */
  summeSchwach?: boolean;
  schritte: BahnSchritt[];
}

export function StationenBahnen({
  titel,
  hinweis,
  bahnen,
  wiedervorlage,
  schritt,
  partei,
  onSchritt,
  onPartei,
}: {
  titel: string;
  hinweis: string;
  bahnen: Bahn[];
  /** Nur Angebote: die ruhende Spalte rechts (2.8). */
  wiedervorlage?: { key: string; anzahl: number; neu: number } | null;
  schritt: string | null;
  partei: BahnId | null;
  onSchritt: (key: string) => void;
  onPartei: (id: BahnId) => void;
}) {
  const { nutzer } = useKora();
  const queryClient = useQueryClient();
  const [wahlFuer, setWahlFuer] = useState<BahnId | null>(null);

  const farben = bahnenfarbenLesen(nutzer?.bahnenFarben);

  const speichern = useMutation({
    mutationFn: (neu: Record<BahnId, string>) =>
      api.aendere<Nutzer>('/profil/darstellung', { bahnenFarben: JSON.stringify(neu) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ich'] });
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });

  function farbeSetzen(id: BahnId, farbe: string) {
    /* Zwei Bahnen nie dieselbe Farbe – belegte sind gar nicht erst wählbar. */
    if (Object.entries(farben).some(([k, f]) => k !== id && f === farbe)) return;
    speichern.mutate({ ...farben, [id]: farbe });
  }

  /*
   * Die Spaltenbreiten: Ein Schritt mit Vorgängen bekommt eine volle Spur,
   * ein leerer nur eine halbe (2.2). Er verschwindet nicht – die Reihenfolge
   * der Schritte ist die Aussage –, nimmt aber niemandem den Platz weg.
   */
  const spalten = bahnen[0]?.schritte ?? [];
  const belegt = (i: number) => bahnen.some((b) => b.schritte[i]?.eigen && (b.schritte[i]?.anzahl ?? 0) > 0);
  /*
   * Die Untergrenze ist `min-content` und nicht die feste Zahl aus 2.2: Die
   * 58 px stehen als `min-width` an der Zelle, das längste Wort darf sie
   * überschreiten. Mit der festen Grenze wurde „Abgenommen" bei 1100 px
   * abgeschnitten – Silbentrennung hilft nur, wo der Browser ein Wörterbuch
   * hat, und darauf darf die Lesbarkeit nicht beruhen.
   */
  const spur = spalten.map((_, i) => (belegt(i) ? 'minmax(min-content, 1fr)' : 'minmax(min-content, .5fr)'));
  const raster = ['112px', ...spur, ...(wiedervorlage ? ['14px', '110px'] : [])].join(' ');
  /*
   * Jede Zelle bekommt ihre Spalte und Zeile ausdrücklich. Ohne das sucht
   * der automatische Satz der Wiedervorlage – sie überspannt alle Bahnen –
   * die erste freie Stelle und landet links statt rechts; das Raster wurde
   * dabei breiter als die Karte.
   */
  const spalteWv = spalten.length + 2;
  const ueberAlle = `1 / span ${bahnen.length}`;

  return (
    <section className="sb">
      <div className="sb-kopf">
        <h2 className="sb-titel">{titel}</h2>
        <span className="sb-hinweis">{hinweis}</span>
      </div>

      {/* Nur das Raster rollt, nicht die Karte – Namen werden nie abgeschnitten. */}
      <div className="sb-rolle">
        <div className="sb-raster" style={{ gridTemplateColumns: raster }} lang="de">
          {bahnen.map((bahn, zeile) => {
            const farbe = farbeVon(farben[bahn.id]);
            const eigene = bahn.schritte.filter((s) => s.eigen);
            const leiter = tonleiter(farbe, eigene.length);
            const gedimmt = partei !== null && partei !== bahn.id;
            return (
              <div key={bahn.id} className="sb-bahn">
                <div
                  className={`sb-kopfzelle ${partei === bahn.id ? 'gewaehlt' : ''} ${gedimmt ? 'gedimmt' : ''}`}
                  style={{ background: farbe.tint, gridColumn: 1, gridRow: zeile + 1 }}
                >
                  <button
                    type="button"
                    className="sb-bahnname"
                    style={{ color: farbe.dunkel }}
                    title={`Auf „${bahn.name}" filtern`}
                    onClick={() => onPartei(bahn.id)}
                  >
                    <span className="sb-bahnlabel">{bahn.name}</span>
                    <span className={`sb-bahnsumme ${bahn.summeSchwach ? 'schwach' : ''}`}>
                      {bahn.summe}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="sb-farbknopf"
                    title={`Farbe für „${bahn.name}"`}
                    aria-label={`Farbe für ${bahn.name}`}
                    onClick={() => setWahlFuer((w) => (w === bahn.id ? null : bahn.id))}
                  >
                    <span className="sb-farbpunkt" style={{ background: farbe.akz }} />
                  </button>
                </div>

                {bahn.schritte.map((s, i) => {
                  const leer = s.anzahl === 0;
                  /* Die Tonleiter zählt nur die eigenen Schritte der Bahn (2.6). */
                  const ton = leiter[eigene.indexOf(s)] ?? farbe.akz;
                  const letzte = i === bahn.schritte.length - 1 && !wiedervorlage;
                  return (
                    <div
                      key={s.key}
                      className={`sb-zelle ${letzte ? 'letzte' : ''} ${gedimmt ? 'gedimmt' : ''}`}
                      style={{ background: farbe.tint, gridColumn: i + 2, gridRow: zeile + 1 }}
                    >
                      {!s.eigen ? null : leer ? (
                        <span className="sb-leer">{s.label}</span>
                      ) : (
                        <button
                          type="button"
                          className={`sb-kachel ${schritt === s.key ? 'gewaehlt' : ''}`}
                          style={{ borderTopColor: ton }}
                          onClick={() => onSchritt(s.key)}
                        >
                          <span className="sb-zahl" style={{ color: farbe.dunkel }}>
                            {s.anzahl}
                          </span>
                          <span className="sb-label">{s.label}</span>
                          {s.neu > 0 && (
                            <span className="sb-neu" title={`${s.neu} neu`}>
                              {s.neu}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Die Wiedervorlage steht einmal über alle Bahnen (2.8). */}
                {wiedervorlage && zeile === 0 && (
                  <>
                    <span className="sb-luecke" style={{ gridColumn: spalteWv, gridRow: ueberAlle }} />
                    <button
                      type="button"
                      className={`sb-ruht ${schritt === wiedervorlage.key ? 'gewaehlt' : ''}`}
                      style={{ gridColumn: spalteWv + 1, gridRow: ueberAlle }}
                      onClick={() => onSchritt(wiedervorlage.key)}
                    >
                      <span className="sb-ruht-kicker">Ruht</span>
                      <span className="sb-ruht-zahl">{wiedervorlage.anzahl}</span>
                      <span className="sb-label">Wiedervorlage</span>
                      {wiedervorlage.neu > 0 && (
                        <span className="sb-neu" title={`${wiedervorlage.neu} neu`}>
                          {wiedervorlage.neu}
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Die Farbwahl (2.7) – eine Leiste unter dem Raster, kein Dialog. */}
      {wahlFuer && (
        <div className="sb-wahl">
          <span className="sb-wahl-titel">
            Farbe für „{bahnen.find((b) => b.id === wahlFuer)?.name}"
            <span className="sb-wahl-note">
              {farbeVon(farben[wahlFuer]).name} · vergebene gedämpft
            </span>
          </span>
          <div className="sb-wahl-kreise">
            {BAHNFARBEN.map((f) => {
              const fremd = Object.entries(farben).some(([k, x]) => k !== wahlFuer && x === f.id);
              const meine = farben[wahlFuer] === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`sb-kreis ${meine ? 'gewaehlt' : ''} ${fremd ? 'belegt' : ''}`}
                  style={{ background: f.tint }}
                  title={fremd ? `${f.name} · schon vergeben` : f.name}
                  disabled={fremd}
                  onClick={() => farbeSetzen(wahlFuer, f.id)}
                >
                  <span className="sb-kreis-punkt" style={{ background: f.akz }}>
                    {meine ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="sb-wahl-zurueck"
            onClick={() => speichern.mutate({ ...BAHNEN_VORGABE })}
          >
            Vorgabe wiederherstellen
          </button>
          <span className="sb-wahl-note">gilt nur für dich</span>
          <button type="button" className="sb-wahl-x" title="Schließen" onClick={() => setWahlFuer(null)}>
            ✕
          </button>
        </div>
      )}
    </section>
  );
}
